import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts/esm/highcharts';
import 'highcharts/esm/highcharts-more';
import 'highcharts/esm/modules/solid-gauge';
import 'highcharts/esm/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import { device } from './Elements';

const GAUGE_COLOR = '#70761e';
const GAUGE_TRACK_COLOR = '#dcdcdc';
const GAUGE_MAX = 10;

const gaugeOptions = (description, level) => ({
  chart: {
    type: 'solidgauge',
    height: 120,
    backgroundColor: 'transparent',
    margin: [0, 0, 0, 0],
    reflow: true,
  },
  title: {
    text: description,
    useHTML: true,
    style: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  },
  accessibility: {
    description: `${description} skill level: ${level} out of ${GAUGE_MAX}`,
  },
  pane: {
    center: ['50%', '85%'],
    size: '150%',
    startAngle: -90,
    endAngle: 90,
    background: {
      backgroundColor: GAUGE_TRACK_COLOR,
      innerRadius: '60%',
      outerRadius: '100%',
      shape: 'arc',
      borderWidth: 0,
    },
  },
  exporting: { enabled: false },
  credits: { enabled: false },
  tooltip: { enabled: false },
  yAxis: {
    min: 0,
    max: GAUGE_MAX,
    lineWidth: 0,
    tickWidth: 0,
    minorTickInterval: null,
    tickAmount: 0,
    labels: { enabled: false },
  },
  plotOptions: {
    solidgauge: {
      dataLabels: {
        y: -10,
        borderWidth: 0,
        useHTML: true,
        format: `<div style="text-align:center;font-size:1rem;font-weight:bold;">${level}/${GAUGE_MAX}</div>`,
      },
    },
  },
  series: [
    {
      data: [{ y: Number(level), color: GAUGE_COLOR }],
      enableMouseTracking: false,
    },
  ],
});

const GridStyles = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media ${device.tablet} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const SkillCard = styled.div`
  text-align: center;
  cursor: default;
  border: 1px solid var(--secondary-color);
  border-radius: 6px;
  padding: 15px 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 4px 4px var(--secondary-color);
  }
`;

const SkillName = styled.div`
  font-size: 0.9rem;
`;

export const Skills = () => {
  const gridRef = useRef(null);
  const chartRefs = useRef([]);

  useEffect(() => {
    let frame;
    const reflowAll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        chartRefs.current.forEach((chart) => chart && chart.reflow());
      });
    };

    const observer = new ResizeObserver(reflowAll);
    if (gridRef.current) observer.observe(gridRef.current);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const skills = [
    {
      id: 'java',
      description: 'Java',
      level: '9',
    },
    {
      id: 'node',
      description: 'NodeJS',
      level: '9',
    },
    {
      id: 'vue',
      description: 'Vue.js',
      level: '9',
    },
    {
      id: 'coldfusion',
      description: 'ColdFusion',
      level: '8',
    },
    {
      id: 'sql',
      description: 'SQL',
      level: '8',
    },
  ];

  return (
    <div>
      <h2>Skills</h2>
      <GridStyles ref={gridRef}>
        {skills.map((skill, index) => (
          <SkillCard key={skill.id}>
            <HighchartsReact
              highcharts={Highcharts}
              options={gaugeOptions(skill.description, skill.level)}
              containerProps={{ style: { width: '100%' } }}
              ref={(instance) => {
                chartRefs.current[index] = instance && instance.chart;
              }}
            />
            <SkillName>{skill.description}</SkillName>
          </SkillCard>
        ))}
      </GridStyles>
    </div>
  );
};
